import type { Subscription, SubscriptionPlan, SubscriptionStatus } from '../types'
import { supabase } from './supabaseClient'
import { getTrackingData, generateEventId } from '../utils/trackingCookies'

type DbSubscription = {
  id: string
  user_id: string
  plan: string
  status: string
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  created_at: string
  updated_at: string
}

type DbUserProfile = {
  user_id: string
  plan: string
  display_name: string | null
  created_at: string
  updated_at: string
}

const mapRow = (row: DbSubscription): Subscription => ({
  id: row.id,
  userId: row.user_id,
  plan: row.plan as SubscriptionPlan,
  status: row.status as SubscriptionStatus,
  stripeCustomerId: row.stripe_customer_id,
  stripeSubscriptionId: row.stripe_subscription_id,
  currentPeriodEnd: row.current_period_end,
  cancelAtPeriodEnd: row.cancel_at_period_end,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
})

/**
 * Get user's plan from user_profile table.
 * This is the source of truth for feature gating.
 * Admins can set this manually to give users Pro access.
 */
export const fetchUserPlan = async (userId: string): Promise<SubscriptionPlan> => {
  const { data, error } = await supabase
    .from('user_profile')
    .select('plan')
    .eq('user_id', userId)
    .maybeSingle()

  if (error && error.code !== 'PGRST116') {
    console.error('fetchUserPlan error:', error)
    // Fallback to free on error
    return 'free'
  }

  if (!data) {
    // No profile yet - return free (profile will be created by trigger)
    return 'free'
  }

  return (data as DbUserProfile).plan as SubscriptionPlan
}

/**
 * Get subscription for a user
 */
export const fetchSubscription = async (userId: string): Promise<Subscription | null> => {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()

  if (error && error.code !== 'PGRST116') {
    console.error('fetchSubscription error:', error)
    throw error
  }

  if (!data) return null
  return mapRow(data as DbSubscription)
}

/**
 * Create a free subscription for a new user (fallback if trigger doesn't fire)
 */
export const ensureSubscription = async (userId: string): Promise<Subscription> => {
  // First try to fetch existing
  const existing = await fetchSubscription(userId)
  if (existing) return existing

  // Create new free subscription
  const { data, error } = await supabase
    .from('subscriptions')
    .insert({
      user_id: userId,
      plan: 'free',
      status: 'active',
    })
    .select()
    .single()

  if (error) {
    // If conflict, fetch again
    if (error.code === '23505') {
      const refetched = await fetchSubscription(userId)
      if (refetched) return refetched
    }
    console.error('ensureSubscription error:', error)
    throw error
  }

  return mapRow(data as DbSubscription)
}

/**
 * Create Stripe checkout session URL
 * This calls a Supabase Edge Function that creates the Stripe session
 */
export const createCheckoutSession = async (
  userId: string,
  priceType: 'monthly' | 'quarterly' | 'lifetime',
  successUrl: string,
  cancelUrl: string,
  eventId?: string
): Promise<string> => {
  const tracking = getTrackingData()
  const resolvedEventId = eventId || generateEventId()

  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: {
      userId,
      priceType,
      successUrl,
      cancelUrl,
      jt_tracking: JSON.stringify(tracking),
      event_id: resolvedEventId,
    },
  })

  if (error) {
    console.error('createCheckoutSession error:', error)
    throw new Error(error.message || 'Failed to create checkout session')
  }

  return data.url
}

/**
 * Create Stripe billing portal URL
 * This calls a Supabase Edge Function that creates the portal session
 */
export const createBillingPortalSession = async (
  userId: string,
  returnUrl: string
): Promise<string> => {
  const { data, error } = await supabase.functions.invoke('create-billing-portal', {
    body: {
      userId,
      returnUrl,
    },
  })

  if (error) {
    console.error('createBillingPortalSession error:', error)
    throw new Error(error.message || 'Failed to create billing portal session')
  }

  return data.url
}

